import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ACCESS_TYPE, AUTH_TOKEN } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';
import { STATUS } from 'constants/miscConstants';

const { SUCCESS, ERROR } = STATUS;

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  redirect: '',
  token: localStorage.getItem(AUTH_TOKEN) || null,
  accessType: localStorage.getItem(ACCESS_TYPE) || null,
  status: SUCCESS,
};

export const signIn = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
  const { email, password } = data;
  try {
    const { token, userType } = await AuthService.login({ email, password });
    localStorage.setItem(AUTH_TOKEN, token);
    localStorage.setItem(ACCESS_TYPE, userType);
    return { token, userType };
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const signOut = createAsyncThunk('auth/logout', async () => {
  const keys = [ACCESS_TYPE, AUTH_TOKEN];
  return keys?.forEach((key) => localStorage.removeItem(key));
});

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (data, { rejectWithValue }) => {
  try {
    const response = await AuthService.forgotPassword(data);
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const resetPasswordRequest = createAsyncThunk('auth/resetPasswordRequest', async (data, { rejectWithValue }) => {
  try {
    const response = await AuthService.resetPassword(data);
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = '/';
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = '';
      state.status = SUCCESS;
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.message = '';
      state.showMessage = false;
      state.redirect = '/';
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        const { token, userType } = action.payload;
        state.loading = false;
        state.redirect = '/';
        state.token = token;
        state.accessType = userType;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = '/';
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = '/';
      })

      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.status = SUCCESS;
        state.message = action.payload.message;
        state.showMessage = true;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(resetPasswordRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPasswordRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.status = SUCCESS;
        state.message = action.payload.message;
        state.showMessage = true;
      })
      .addCase(resetPasswordRequest.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      });
  },
});

export const { authenticated, showAuthMessage, hideAuthMessage, showLoading, signOutSuccess } = authSlice.actions;

export default authSlice.reducer;
